import { Button } from "../../ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../ui/form";
import { Input } from "../../ui/input";
import { Alert, AlertTitle } from "../../ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { vLLMChatSettings } from "@/types/ChatSettings";
import { forwardRef, useImperativeHandle, useEffect } from "react";
import { settingsValidator, ChatSettings } from "@shared/validators/admin/settings-validators";

type Props = {
    data?: vLLMChatSettings;
    initialData?: any;
    onDataChange?: (data: any) => void;
    readOnly?: boolean;
    error?: string;
    submitButton?: React.ReactNode;
    onSubmit?: (data: any) => void;
    hideSubmitButton?: boolean;
};

// Changed component name to match capitalization convention in React
const VLLMChatSettingsForm = forwardRef(
    (
        { data, initialData = {}, onDataChange, submitButton, readOnly, hideSubmitButton, error, onSubmit }: Props,
        ref
    ) => {
        const form = useForm<ChatSettings>({
            resolver: zodResolver(settingsValidator.chatSettings),
            defaultValues: {
                VLLM_MODEL: initialData?.VLLM_MODEL || data?.VLLM_MODEL || "",
                VLLM_BASE_URL: initialData?.VLLM_BASE_URL || data?.VLLM_BASE_URL || "",
            },
        });

        // Watch for changes and notify parent component
        useEffect(() => {
            const subscription = form.watch((value) => {
                if (onDataChange) {
                    onDataChange(value);
                }
            });

            return () => subscription.unsubscribe();
        }, [form, onDataChange]);

        // Expose methods to parent component
        useImperativeHandle(ref, () => ({
            validateForm: async () => {
                const result = await form.trigger();
                if (!result) {
                    // Get the first error message
                    const errors = form.formState.errors;
                    const firstError = Object.values(errors)[0];
                    if (firstError?.message) {
                        console.error("VLLM Chat validation error:", firstError.message);
                    }
                }
                return result;
            },
            getFormData: () => {
                return form.getValues();
            },
            submitForm: () => {
                if (onSubmit) {
                    form.handleSubmit(onSubmit)();
                }
            },
        }));

        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit((data) => onSubmit && onSubmit(data))}>
                    <div>
                        {error && (
                            <Alert variant="destructive" className="mb-2 dark:bg-red-800 dark:text-white">
                                <div className="flex items-center gap-2">
                                    <ExclamationTriangleIcon className="h-4 w-4" />
                                    <AlertTitle className="mb-0">{error}</AlertTitle>
                                </div>
                            </Alert>
                        )}
                        <div className="space-y-3">
                            <FormField
                                control={form.control}
                                name="VLLM_MODEL"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel htmlFor={field.name}>Model Name</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                readOnly={readOnly}
                                                placeholder="e.g., meta-llama/Llama-2-13b-chat-hf"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="VLLM_BASE_URL"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel htmlFor={field.name}>Base URL</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                readOnly={readOnly}
                                                placeholder="e.g., http://localhost:8000/v1"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    {submitButton ? (
                        submitButton
                    ) : hideSubmitButton ? null : (
                        <div className="flex justify-end pt-10">
                            <Button type="submit">Save Changes</Button>
                        </div>
                    )}
                </form>
            </Form>
        );
    }
);

// Update displayName to match the new component name
VLLMChatSettingsForm.displayName = "VLLMChatSettingsForm";

export default VLLMChatSettingsForm;
